import React, {
  useMemo,
  cloneElement,
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";
import PropTypes from "prop-types";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { useNavigate, useLocation } from "react-router-dom";
// import Slide from "@mui/material/Slide";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { isTelegramMiniApp } from "core/utils/telegram";
import { useMatch } from "react-router-dom";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

const RouteDialogContext = createContext();

export { RouteDialogContext };

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? "rgba(0, 0, 0, 0.2)"
      : "rgba(255, 255, 255, 0.5)",
  position: "absolute",
  top: 10,
  zIndex: 10000,
  "&:hover": {
    background:
      theme.palette.mode === "dark"
        ? "rgba(0, 0, 0, 0.3)"
        : "rgba(255, 255, 255, 0.6)",
  },
}));

export default function RouteDialog({ path, element, fullscreen = false }) {
  const match = useMatch(path);
  const [lastMatch, setLastMatch] = useState(match);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Полноэкранный режим только на мобилках
  const isFullscreen = Boolean(fullscreen && isMobile);

  useEffect(() => {
    if (match) {
      setLastMatch(match);
    }
  }, [match]);

  const open = Boolean(match);

  const clonedElement = useMemo(
    () => cloneElement(element, lastMatch),
    [element, lastMatch]
  );

  const onClose = useCallback(() => {
    if (location?.state?.background) {
      /**
       * идем назад, не в background
       * так как это могла быть цепочка диалогов
       * продукт => контент
       */
      navigate(
        -1
        // location.state.background.pathname + location.state.background.search
      );
    } else {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    if (!process.browser) return;
    if (isFullscreen) return; // Не скроллим в полноэкранном режиме

    const routeDialogScrollable = document.querySelector(
      "#route-dialog-scrollable"
    );

    routeDialogScrollable?.scrollTo(0, 0);
  }, [location.pathname, isFullscreen]);

  const contextValue = {
    onClose,
  };

  const DrawerComponent = isFullscreen ? Drawer : SwipeableDrawer;

  return (
    <DrawerComponent
      test-id={open ? "route-dialog-open" : "route-dialog-closed"}
      open={open}
      anchor="bottom"
      onClose={onClose}
      elevation={0}
      // Размонтируем контент только после завершения анимации закрытия.
      // Обязательно для обеих веток (Drawer и SwipeableDrawer): иначе при
      // keepMounted страница с плеером остаётся жить и играть в фоне.
      onTransitionExited={() => setLastMatch(null)}
      {...(!isFullscreen
        ? {
            onOpen: () => {
              console.log("onOpen");
            },
            disableSwipeToOpen: true,
            disableDiscovery: true,
            allowSwipeInChildren: true,
          }
        : {})}
      // hideBackdrop
      disablePortal // это нужно чтобы в миниапе не прыгало
      disableAutoFocus // это нужно чтобы в миниапе не прыгало
      disableRestoreFocus
      disableEscapeKeyDown // отключаем закрытие по Escape для Telegram fullscreen
      keepMounted // это нужно чтобы в миниапе не прыгало
      sx={{
        zIndex: isFullscreen ? 1300 : 1099, // Выше header для полноэкранного режима
        top: isFullscreen ? 0 : { xs: 64, md: 64 }, // Полный экран или с отступом
        "& > .MuiDrawer-paper": {
          bgcolor: isFullscreen ? "#000" : theme.palette.background.body, // Черный фон для полноэкранного режима
          position: "absolute",
          top: 0,
          overflowY: isFullscreen ? "hidden" : "auto", // Отключаем overflow для полноэкранного режима
          height: isFullscreen ? "100%" : "auto", // Полная высота для полноэкранного режима
        },

        "& > .MuiModal-backdrop": {
          bgcolor: isFullscreen ? "#000" : theme.palette.background.body, // Черный backdrop для полноэкранного режима
        },
      }}
    >
      <RouteDialogContext.Provider value={contextValue}>
        {lastMatch && (
          <Box
            sx={{
              overflowY: isFullscreen ? "hidden" : "auto",
              height: isFullscreen ? "100%" : "auto",
            }}
            // id={open ? "route-dialog-scrollable" : undefined}
          >
            {clonedElement}
          </Box>
        )}
      </RouteDialogContext.Provider>
    </DrawerComponent>
  );
}

RouteDialog.propTypes = {
  path: PropTypes.string.isRequired,
  element: PropTypes.node.isRequired,
  fullscreen: PropTypes.bool,
};

export function CloseRouteDialogButton({ position = "left" }) {
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const context = useContext(RouteDialogContext);

  if (isTelegramMiniApp) return null;
  if (!context) return null;

  return (
    <StyledIconButton
      size={sm ? "medium" : "medium"}
      onClick={context.onClose}
      sx={{
        left: position === "left" ? 10 : "auto",
        right: position === "right" ? 10 : "auto",
      }}
    >
      <ExpandMoreIcon fontSize={sm ? "large" : "medium"} />
    </StyledIconButton>
  );
}

CloseRouteDialogButton.propTypes = {
  position: PropTypes.oneOf(["left", "right"]),
};
